import { useState } from 'react';
import SimpleSelect from '@/components/common/SimpleSelect';

interface GstRateOption {
  id: string;
  name: string;
  rate: number;
}

interface BeforeAfterTaxPriceFieldProps {
  /** e.g. "Purchase Price" — the surrounding currency symbol is added automatically. */
  label: string;
  /** Stored tax-exclusive base value (what gets persisted). */
  baseValue?: number;
  currencySymbol: string;
  /**
   * The item's single GST rate — shared between Purchase and Sales. Render
   * this component once per price with the SAME gstRateId/gstRateOptions/
   * onRateChange (backed by one `formData.gstRateId`); picking a rate in one
   * instance updates the other automatically since they share state.
   */
  gstRateId?: string;
  gstRateOptions: GstRateOption[];
  onRateChange: (gstRateId: string | undefined) => void;
  /** Always receives the recomputed base value, plus whether After Tax was the field just edited. */
  onChange: (baseValue: number | undefined, includesTax: boolean) => void;
}

const round2 = (n: number) => Math.round(n * 100) / 100;

/**
 * A price entered as either its tax-exclusive ("Before Tax") or tax-inclusive
 * ("After Tax (NET)") amount, with the item's GST rate dropdown embedded
 * between them — editing either price side live-recalculates the other from
 * the selected rate. Only the exclusive base is ever persisted (via
 * `onChange`); "After Tax (NET)" is always a pure derived view of (base,
 * rate), so it stays correct on reopen and after the GST rate changes.
 *
 * While a field is actively being typed into, it shows the raw keystrokes
 * rather than a recomputed-and-rounded echo of itself — avoids the number
 * visibly jittering (e.g. "1000" flashing to "999.9999999998") as the other
 * field's rounded value round-trips back through the tax formula.
 *
 * Duplicated from app/src/components/items/BeforeAfterTaxPriceField.tsx — this
 * standalone admin/ portal is a separate deployable package and cannot import
 * across app boundaries.
 */
export default function BeforeAfterTaxPriceField({
  label,
  baseValue,
  currencySymbol,
  gstRateId,
  gstRateOptions,
  onRateChange,
  onChange,
}: BeforeAfterTaxPriceFieldProps) {
  const [activeField, setActiveField] = useState<'before' | 'after' | null>(null);
  const [rawText, setRawText] = useState('');

  const gstPercentage = gstRateOptions.find((r) => r.id === gstRateId)?.rate || 0;
  const rate = gstPercentage;
  const afterFromBase = baseValue !== undefined ? round2(baseValue * (1 + rate / 100)) : undefined;

  const beforeDisplay = activeField === 'before' ? rawText : (baseValue ?? '');
  const afterDisplay = activeField === 'after' ? rawText : (afterFromBase ?? '');

  const handleBeforeChange = (text: string) => {
    setActiveField('before');
    setRawText(text);
    const value = text ? parseFloat(text) : undefined;
    onChange(value !== undefined && !isNaN(value) ? value : undefined, false);
  };

  const handleAfterChange = (text: string) => {
    setActiveField('after');
    setRawText(text);
    const value = text ? parseFloat(text) : undefined;
    if (value === undefined || isNaN(value)) {
      onChange(undefined, true);
      return;
    }
    const base = rate > 0 ? round2(value / (1 + rate / 100)) : value;
    onChange(base, true);
  };

  const clearActive = () => setActiveField(null);

  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-2">
        {label} ({currencySymbol})
      </label>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
        <div>
          <span className="block text-[11px] text-gray-500 mb-1">Before Tax</span>
          <input
            type="number"
            step="0.01"
            min="0"
            value={beforeDisplay}
            onChange={(e) => handleBeforeChange(e.target.value)}
            onBlur={clearActive}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            placeholder="0.00"
          />
        </div>
        <div>
          <span className="block text-[11px] text-gray-500 mb-1">Tax</span>
          <SimpleSelect
            value={gstRateId || ''}
            onChange={(v) => onRateChange(v || undefined)}
            options={[
              { value: '', label: 'No tax' },
              ...gstRateOptions.map((r) => ({ value: r.id, label: `${r.name} - ${r.rate}%` })),
            ]}
            placeholder="No tax"
            aria-label="GST rate"
            wrapperClassName="w-full min-w-0"
            className="focus:!ring-blue-500 focus:!border-blue-500"
          />
        </div>
        <div>
          <span className="block text-[11px] text-gray-500 mb-1">After Tax (NET)</span>
          <input
            type="number"
            step="0.01"
            min="0"
            value={afterDisplay}
            onChange={(e) => handleAfterChange(e.target.value)}
            onBlur={clearActive}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            placeholder="0.00"
          />
        </div>
      </div>
    </div>
  );
}
